/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { Crosshair, Folder } from 'lucide-react';

import { Button } from '@/components/ui/button';
import ChatGuide from '@/sections/insights/aiAssistant/ChatGuide';
import { getConversationModes } from '@/sections/insights/aiAssistant/config/storage';
import ConversationList from '@/sections/insights/aiAssistant/ConversationList';
import type {
  AIAssistantMode,
  SidebarTab,
} from '@/sections/insights/aiAssistant/types';

import { SidebarHuntsTab } from './SidebarHuntsTab';

interface SidebarProps {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  activeTab: SidebarTab;
  setActiveTab: (tab: SidebarTab) => void;
  mode: AIAssistantMode;
  isAIDisabledButBypassed: boolean;
  isInputDisabled: boolean;
  conversations: any[];
  modeFilteredConversations: any[];
  conversationPreviews: Record<string, string>;
  selectedConversationId: string | null;
  onCreateConversation: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onExampleClick: (query: string, sendImmediately?: boolean) => Promise<void>;
  onModeChange: (mode: AIAssistantMode) => void;
  /**
   * Presentation-only gate for the Hunts tab affordance. When false the Hunts
   * tab button is not rendered in either the collapsed or expanded sidebar, so
   * a customer who is not entitled never sees a dead-end empty Hunts view.
   * Server remains the source of truth for hunt data (see AIAssistant.canViewHunts).
   */
  showHunts: boolean;
  hunts: any[];
  activeHuntUuid: string | null;
  onNewHunt: () => void;
}

export function Sidebar({
  isSidebarCollapsed,
  toggleSidebar,
  activeTab,
  setActiveTab,
  mode,
  isAIDisabledButBypassed,
  conversationPreviews,
  modeFilteredConversations,
  selectedConversationId,
  onCreateConversation,
  onSelectConversation,
  onDeleteConversation,
  onExampleClick,
  onModeChange,
  showHunts,
  hunts,
  activeHuntUuid,
  onNewHunt,
}: SidebarProps) {
  return (
    <div
      className={`border-chariot-stroke relative flex flex-col border-r transition-all duration-300 ${
        isSidebarCollapsed ? 'w-16' : 'w-72'
      }`}
    >
      {/* Collapse/expand toggle — vertically centered when expanded. */}
      <div
        className={`absolute -right-3 z-10 ${
          isSidebarCollapsed ? 'top-4' : 'top-1/2 -translate-y-1/2'
        }`}
      >
        <button
          onClick={toggleSidebar}
          disabled={isAIDisabledButBypassed}
          className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary flex size-6 items-center justify-center rounded-full border shadow-xs transition-colors ${isAIDisabledButBypassed ? 'text-chariot-stroke cursor-not-allowed' : 'hover:text-chariot-text-primary'}`}
        >
          {isSidebarCollapsed ? (
            <ChevronRightIcon className="size-3" />
          ) : (
            <ChevronLeftIcon className="size-3" />
          )}
        </button>
      </div>

      {isSidebarCollapsed ? (
        <div className="-ml-3 flex h-full flex-col items-center space-y-6 py-6">
          <button
            onClick={onCreateConversation}
            disabled={isAIDisabledButBypassed}
            className={`flex size-10 items-center justify-center rounded-lg border shadow-xs transition-colors ${isAIDisabledButBypassed ? 'border-chariot-stroke text-chariot-text-secondary cursor-not-allowed opacity-40' : 'border-chariot-brand-primary text-chariot-text-primary hover:bg-chariot-background-secondary'}`}
            title="New Conversation"
          >
            <PlusIcon className="size-5" />
          </button>
          <div className="bg-chariot-stroke h-px w-6"></div>
          <button
            onClick={() => {
              if (!isAIDisabledButBypassed) {
                if (mode === 'auto') {
                  onModeChange('agent');
                } else {
                  setActiveTab('conversations');
                }
                toggleSidebar();
              }
            }}
            disabled={isAIDisabledButBypassed}
            className={`relative flex size-9 items-center justify-center transition-all ${
              isAIDisabledButBypassed
                ? 'cursor-not-allowed opacity-40'
                : activeTab === 'conversations'
                  ? 'text-chariot-brand-primary'
                  : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            }`}
            title="Conversations"
          >
            <Folder className="size-5" />
          </button>
          <button
            onClick={() => {
              if (!isAIDisabledButBypassed) {
                setActiveTab('guide');
                toggleSidebar();
              }
            }}
            disabled={isAIDisabledButBypassed}
            className={`flex size-9 items-center justify-center transition-all ${
              isAIDisabledButBypassed
                ? 'cursor-not-allowed opacity-40'
                : activeTab === 'guide'
                  ? 'text-chariot-brand-primary'
                  : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            }`}
            title="Prompt Library"
          >
            <QuestionMarkCircleIcon className="size-5" />
          </button>
          {showHunts && (
            <button
              onClick={() => {
                if (!isAIDisabledButBypassed) {
                  onModeChange('auto');
                  toggleSidebar();
                }
              }}
              disabled={isAIDisabledButBypassed}
              className={`flex size-9 items-center justify-center transition-all ${
                isAIDisabledButBypassed
                  ? 'cursor-not-allowed opacity-40'
                  : activeTab === 'hunts'
                    ? 'text-chariot-brand-primary'
                    : 'text-chariot-text-secondary hover:text-chariot-text-primary'
              }`}
              title="Hunts"
            >
              <Crosshair className="size-5" />
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="border-chariot-stroke flex border-b">
            <button
              onClick={() => {
                if (mode === 'auto') {
                  onModeChange('agent');
                } else {
                  setActiveTab('conversations');
                }
              }}
              title="Conversations"
              className={`relative flex flex-1 items-center justify-center py-3 transition-colors ${
                activeTab === 'conversations'
                  ? 'text-chariot-brand-primary'
                  : 'text-chariot-text-secondary hover:text-chariot-text-primary'
              }`}
            >
              <Folder className="size-5" />
              {activeTab === 'conversations' && (
                <span className="bg-chariot-brand-primary absolute right-3 bottom-0 left-3 h-0.5 rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              title="Prompts"
              className={`relative flex flex-1 items-center justify-center py-3 transition-colors ${
                activeTab === 'guide'
                  ? 'text-chariot-brand-primary'
                  : 'text-chariot-text-secondary hover:text-chariot-text-primary'
              }`}
            >
              <QuestionMarkCircleIcon className="size-5" />
              {activeTab === 'guide' && (
                <span className="bg-chariot-brand-primary absolute right-3 bottom-0 left-3 h-0.5 rounded-full" />
              )}
            </button>
            {showHunts && (
              <button
                onClick={() => onModeChange('auto')}
                title="Hunts"
                className={`relative flex flex-1 items-center justify-center py-3 transition-colors ${
                  activeTab === 'hunts'
                    ? 'text-chariot-brand-primary'
                    : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                }`}
              >
                <Crosshair className="size-5" />
                {activeTab === 'hunts' && (
                  <span className="bg-chariot-brand-primary absolute right-3 bottom-0 left-3 h-0.5 rounded-full" />
                )}
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="flex min-h-0 flex-1 flex-col">
            {activeTab === 'conversations' && (
              <>
                <div className="border-chariot-stroke border-b px-4 py-3">
                  <Button
                    onClick={onCreateConversation}
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full"
                  >
                    <PlusIcon className="size-4" />
                    New Conversation
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  <ConversationList
                    conversations={modeFilteredConversations}
                    activeConversationId={selectedConversationId}
                    conversationPreviews={conversationPreviews}
                    onDeleteConversation={onDeleteConversation}
                    onSelectConversation={onSelectConversation}
                    conversationModes={(() => {
                      const modes = getConversationModes();
                      const result: Record<string, 'query' | 'agent' | 'auto'> =
                        {};
                      for (const [id, data] of Object.entries(modes)) {
                        result[id] = data.mode;
                      }
                      return result;
                    })()}
                  />
                </div>
              </>
            )}
            {activeTab === 'guide' && (
              <>
                <div className="border-chariot-stroke border-b px-4 py-3">
                  <Button
                    onClick={onCreateConversation}
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full"
                  >
                    <PlusIcon className="size-4" />
                    New Conversation
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <ChatGuide
                    onPromptClick={query => onExampleClick(query, false)}
                  />
                </div>
              </>
            )}
            {activeTab === 'hunts' && (
              <SidebarHuntsTab
                hunts={hunts}
                activeHuntUuid={activeHuntUuid}
                onNewHunt={onNewHunt}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
